import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import CustomAlert from "./CustomAlert";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";

const AlertContext = createContext(null);

export function AlertProvider({ children }) {
  const [alertState, setAlertState] = useState({
    show: false,
    title: "Oops!",
    message: "",
    type: "success"
  });

  const hideAlert = useCallback(() => {
    setAlertState((prev) => ({ ...prev, show: false }));
  }, []);

  const showAlert = useCallback((messageOrOptions, maybeTitle, type) => {
    if (
      messageOrOptions &&
      typeof messageOrOptions === "object" &&
      !Array.isArray(messageOrOptions) &&
      ("message" in messageOrOptions || "title" in messageOrOptions)
    ) {
      setAlertState({
        show: true,
        title: messageOrOptions.title || "Oops!",
        message: messageOrOptions.message ?? "",
        type: messageOrOptions.type || type,
      });
      return;
    }

    setAlertState({
      show: true,
      title: maybeTitle || "Oops!",
      message: messageOrOptions ?? "",
      type
    });
  }, []);

  useBodyScrollLock(alertState.show);

  useEffect(() => {
    if (!alertState.show) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") hideAlert();
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [alertState.show, hideAlert]);

  const value = useMemo(
    () => ({
      showAlert,
      hideAlert,
    }),
    [showAlert, hideAlert]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      <CustomAlert
        show={alertState.show}
        onHide={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
      />
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
}

export default AlertContext;
